import { useRef, useState } from 'react';
import classnames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import { useSpring, animated } from 'react-spring';
import { useMeasure } from 'react-use';

import { useMedia } from 'utils/hooks';

import { Mousewheel, FreeMode } from 'swiper';
import type { Swiper as SwiperType } from 'swiper';

import arrowIcon from 'images/icons/highlights-open-white.png';

import type { Tour } from '../../../../types/matterport-sdk';

import 'swiper/css'; // eslint-disable-line import/no-unresolved
import styles from './index.css';

export interface HighlightSliderProp {
    className?: string;
    active?: boolean;
    highlights?: Tour.Snapshot[];
    onClick?: (highlightIndex: number) => void;
}

const HighlightSlider: React.FC<HighlightSliderProp> = ({
    className,
    active = true,
    highlights = [],
    onClick = () => {},
}) => {
    const swiper = useRef<SwiperType | null>(null);

    const { mediaScale } = useMedia();

    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const [locked, setLocked] = useState(false);

    const [ref, { width }] = useMeasure<HTMLDivElement>();

    const { maskHeight } = useSpring({ maskHeight: active ? (55 + 28) * mediaScale : 0 });

    return (
        <animated.div
            className={classnames(styles.highlightSlider, className)}
            style={{ height: maskHeight }}
        >
            <div ref={ref} className={styles.wrapper}>
                <button
                    className={classnames(
                        styles.action,
                        styles.prev,
                        (locked || isBeginning) && styles.disabled,
                    )}
                    type="button"
                    onClick={() => {
                        swiper.current?.slidePrev();
                    }}
                >
                    <img src={arrowIcon} alt="prev" />
                </button>
                <Swiper
                    className={styles.swiperContainer}
                    slidesPerView="auto"
                    spaceBetween={27 * mediaScale}
                    mousewheel
                    freeMode
                    modules={[FreeMode, Mousewheel]}
                    onSwiper={(nextSwiper) => {
                        swiper.current = nextSwiper;
                    }}
                    onProgress={(_, progress) => {
                        setIsBeginning(progress === 0);
                        setIsEnd(progress === 1);
                    }}
                    onLock={() => {
                        setLocked(true);
                    }}
                    onUnlock={() => {
                        setLocked(false);
                    }}
                >
                    {highlights.map((highlight, index) => (
                        <SwiperSlide
                            key={highlight.sid}
                            className={styles.slide}
                            style={
                                index === highlights.length - 1
                                    ? {
                                          width:
                                              width -
                                              highlights.length * (85 + 27) * mediaScale -
                                              40 * mediaScale,
                                      }
                                    : {}
                            }
                            onClick={() => {
                                onClick(index);
                            }}
                        >
                            <div
                                className={styles.image}
                                style={{ backgroundImage: `url(${highlight.thumbnailUrl})` }}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
                <button
                    className={classnames(
                        styles.action,
                        styles.next,
                        (locked || isEnd) && styles.disabled,
                    )}
                    type="button"
                    onClick={() => {
                        swiper.current?.slideNext();
                    }}
                >
                    <img src={arrowIcon} alt="next" />
                </button>
            </div>
        </animated.div>
    );
};

export default HighlightSlider;
